import type { Game, GameLicense } from 'shared/modelTypes/index';
import { Button } from './Button';
import QRCode from 'qrcode';
import { Avatar } from './Avatar';
import { InlineCode } from './InlineCode';
import { idToQr, onUnmount, unreachable } from 'shared/util';
import { PlayTeamKleuren } from './PlayTeamKleuren';
import { rpc } from 'src/backend';
import { navigate, type Path } from 'engine/router';
import { PlayKwaliteitenJacht } from './PlayKwaliteitenJacht';
import { PlayZintuigen } from './PlayFacilitaireZintuigen';
import { PlayJackpot } from './PlayJackpot';
import { PlayVingerAfdrukken } from './PlayVingerAfdrukken';
import { Modal, showModal } from './Modal';
import { PlayReflectora } from './PlayReflectora';
import { PlayBouwHetLandschap } from './PlayBouwHetLandschap';
import { PlayFlikken } from './PlayFlikken';
import { PlayPlayAndConnect } from './PlayPlayAndConnect';

type GameLicenseViewProps = {
	license: GameLicense;
	game: Game;
};
export function GamePlay({ game, license }: GameLicenseViewProps) {
	const view: HTMLElement = (
		<div class="mx-auto rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800">
			<div class="mb-4 flex items-center justify-between">
				<div class="flex items-center gap-4">
					<Avatar src={`/images/${game.kind}.jpg`} alt="game icon" />
					<div>
						<h2 class="text-xl font-bold text-gray-800 dark:text-gray-100">
							Playing {license.name ? license.name + ' - ' : ''}
							{game.name ?? game.kind}
						</h2>
						<small duration></small>
					</div>
				</div>
			</div>
			<div>
				<p>
					Code: <InlineCode clipboardButton>{idToQr(license.code ? license.code : parseInt(license._key!))}</InlineCode>
				</p>
				<small>Spelers kunnen meedoen met deze code.</small>

				<img qr />

				<Button
					mode="secondary"
					on:click={async () => {
						showModal(
							<Modal
								title="Spel stoppen"
								description="Weet je zeker dat je het spel wilt stoppen? Spelers zullen direct worden uitgelogd, hervatten van het spel is niet mogelijk."
								confirm={'Spel stoppen'}
								cancel={'Toch doorspelen'}
							/>,
						).then(async (confirm) => {
							if (!confirm) return;
							await rpc.endPlaySession.query({ licenseId: license._id! });
							navigate(`/dashboard/licenses/${license._key!}` as Path);
						});
					}}
				>
					Spel stoppen
				</Button>
			</div>
			<hr class="my-3" />

			{(() => {
				const kind = game.kind;
				switch (kind) {
					case 'team-kleuren-spel':
						return <PlayTeamKleuren licenseCode={license._key!} />;
					case 'kwaliteiten-jacht':
						return <PlayKwaliteitenJacht licenseCode={license._key!} />;
					case 'facilitaire-zintuigen-spel':
						return <PlayZintuigen licenseCode={license._key!} />;
					case 'jackpot-van-las-vegas':
						return <PlayJackpot licenseCode={license._key!} />;
					case 'vinger-afdrukken-spel':
						return <PlayVingerAfdrukken licenseCode={license._key!} />;
					case 'bouw-het-landschap-spel':
						return <PlayBouwHetLandschap licenseCode={license._key!} />;
					case 'reflectora-spel':
						return <PlayReflectora licenseCode={license._key!} />;
					case 'flikken-challenge':
						return <PlayFlikken licenseCode={license._key!} />;
					case 'play-and-connect-spel':
						return <PlayPlayAndConnect licenseCode={license._key!} />;
					default:
						unreachable(kind);
				}
			})()}
		</div>
	);

	const qrContainer = view.querySelector('[qr]') as HTMLImageElement;

	QRCode.toDataURL(import.meta.env.VITE_QRCODE_URL + idToQr(license.code ? license.code : parseInt(license._key!))).then(
		(qr) => (qrContainer.src = qr),
	);

	const durationView = view.querySelector('[duration]') as HTMLElement;

	const updateDuration = () => {
		const lastEvent = license.playSessions.at(-1);
		const from = new Date(lastEvent?.from || 0);
		const now = new Date();
		const diff = Math.max(0, now.getTime() - from.getTime());

		const hours = String(Math.floor(diff / 3600000)).padStart(2, '0');
		const minutes = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
		const seconds = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');

		durationView.textContent = `Speeltijd: ${hours}:${minutes}:${seconds}`;
	};

	const interval = setInterval(updateDuration, 1000);
	updateDuration(); // Initialize immediately
	onUnmount(durationView, () => {
		clearInterval(interval);
	});

	return view;
}
